import {Box, Button, styled} from "@mui/material";
import Base from "../../../components/base";
import {buildPath, formatDateInfo, formatTime, getFasciaRange, scale} from "../../../utils";
import BaseHeader from "../../../components/base/header";
import BaseTitle from "../../../components/base/title";
import LABEL from "../../../resources/label";
import {useNavigate, useParams} from "react-router-dom";
import DateDetailedCard from "../../../components/date-detailed-card";
import Text from "../../../components/text";
import theme, {FONTS} from "../../../constants/theme";
import React, {useCallback, useEffect, useState} from "react";
import DonorList from "../../../components/donor-list";
import DonorSearch from "../../../components/donor-search";
import DateRowInfo from "../../../components/date-row-info";
import CustomSvg from "../../../components/custom-svg";
import {GOCCIA_RIFLESSO} from "../../../constants/svgs";
import {
  addNotaToRaccolta,
  deleteRaccoltaById,
  fetchAllDonatoriRaccoltaById,
  fetchRaccoltaById,
  removeDonorFromRaccolta
} from "../../../features/operator";
import {DeleteDonorRequest, DonatoreResponse, DonorTypeFilter, RaccoltaResponse} from "../../../types";
import FullScreenDialog from "../../../components/full-screen-dialog";

const Wrapper = styled(Box)({

});

const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  marginTop: scale(65),
  height: scale(480 + 138),
  width: scale(320 + 40 + 753.14),
});

const DonorsWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: scale(730),
  height: scale(79 + 515),
});

const DettaglioRaccolta = () => {
  const navigate = useNavigate();
  const { raccoltaId } = useParams<{ raccoltaId: string }>();
  const [raccolta, setRaccolta] = useState<RaccoltaResponse | null>(null)
  const [listaDonatori, setListaDonatori] = useState<DonatoreResponse[] | null>(null)
  const [textSearch, setTextSearch] = useState("");
  const [filter, setFilter] = useState<DonorTypeFilter>('plasma & sangue');
  const [deleteRaccoltaDialogOpen, setDeleteRaccoltaDialogOpen] = useState(false);
  const [deleteDonorDialogOpen, setDeleteDonorDialogOpen] = useState(false);
  const [addNotaDialogOpen, setAddNotaDialogOpen] = useState(false);
  const [deleteDonorData, setDeleteDonorData] = useState<DeleteDonorRequest | null>(null);
  const [getCSVBlob, setGetCSVBlob] = useState<(Blob) | null>(null);

  const handleDownload = () => {
    if (!getCSVBlob || !raccolta) return;


    const url = URL.createObjectURL(getCSVBlob);
    const link = document.createElement('a');
    link.href = url;

    const formattedDate = new Date(raccolta.date).toISOString().split('T')[0];
    link.download = `donatori-${raccolta.name}-${formattedDate}.csv`.replace(/\s+/g, '_');
    link.click();
    URL.revokeObjectURL(url);
  };


  const loadRaccoltaData = useCallback(() => {
    if (!raccoltaId) return;

    fetchRaccoltaById(Number(raccoltaId)).then((res) => {
      setRaccolta(res);
    });

    fetchAllDonatoriRaccoltaById(Number(raccoltaId)).then((res) => {
      const listaDonatoriModificata = res.map(donatore => ({
        ...donatore,
        hour: formatTime(donatore.hour),
        CF: donatore.CF ?? '',
      }));
      setListaDonatori(listaDonatoriModificata);
    });
  }, [raccoltaId]);

  useEffect(() => {
    loadRaccoltaData();
  }, [loadRaccoltaData, raccoltaId]);

  if (!raccolta || !listaDonatori) {
    return null;
  }

  const dateInfo = formatDateInfo(raccolta.date)

  return (
    <>
      <FullScreenDialog
        title={'AGGIUNGI NOTA'}
        content={'Inserisci qui il testo di avviso che vuoi che appaia al donatore una volta selezionata la raccolta. Massimo 250 caratteri'}
        open={addNotaDialogOpen}
        onClose={() => setAddNotaDialogOpen(false)}
        onConfirm={(input) => {
          if (raccoltaId) {
            addNotaToRaccolta({raccoltaId: Number(raccoltaId), nota: input}).then(() => {
              setAddNotaDialogOpen(false)
            })
          }
        }}
        hasTextInput={true}
        contentSx={{
          fontsize: scale(18),
          fontWeight: 300,
          color: theme.palette.white.main
        }}
        colorStyle={'blue'}
        cancelText={'ANNULLA'}
        confirmText={'CONFERMA'}
      />
      <FullScreenDialog
        title={'VUOI ELIMINARE LA RACCOLTA?'}
        content={'Se prosegui con l’eliminazione l’evento verrà rimosso dal calendario e perderai tutti i dati legati alla raccolta.'}
        open={deleteRaccoltaDialogOpen}
        onClose={() => setDeleteRaccoltaDialogOpen(false)}
        onConfirm={() => {
          handleDownload()
          if (raccoltaId) {
            deleteRaccoltaById(Number(raccoltaId)).then(() => {
              setDeleteRaccoltaDialogOpen(false)
              navigate(buildPath(`/operatore/calendario`))
            })
          }
        }}
        hasTextInput={true}
        placeholder={'Digita ELIMINA per proseguire'}
        colorStyle={'red'}
        cancelText={'ANNULLA'}
        confirmText={'ELIMINA'}
      />
      <FullScreenDialog
        title={'VUOI ELIMINARE IL DONATORE DALLA RACCOLTA?'}
        content={'Se prosegui il donatore selezionato\n verrà rimosso dalla lista donatori della\n raccolta.'}
        open={deleteDonorDialogOpen}
        onClose={() => setDeleteDonorDialogOpen(false)}
        onConfirm={() => {
          if (deleteDonorData) {
            removeDonorFromRaccolta(deleteDonorData).then(() => {
              setDeleteDonorDialogOpen(false)
              loadRaccoltaData()
            })
          }
        }}
        colorStyle={'red'}
        cancelText={'ANNULLA'}
        confirmText={'ELIMINA'}
      />
      <Wrapper>
        <Base style={{ height: scale(850), width: scale(1200)}}>
          <BaseHeader>
            <Button
              sx={{
                visibility: 'none',
                height: scale(26),
                '&:hover': {
                  cursor: 'pointer',
                }
              }}
              onClick={() => navigate(buildPath(`/operatore/dettaglio-raccolta/${raccoltaId}/aggiungi-donatore`))}
            >
            <Text
              text={'Aggiungi donatore'}
              color={theme.palette.text.secondary}
              fontFamily={FONTS.MONTSERRAT}
              fontWeight={600}
              fontSize={scale(18)}
              sx={{
                textTransform: 'uppercase', textDecoration: 'underline',
              }}
            />
            </Button>
            {(raccolta.nota === null || raccolta.nota.trim() === '') && (<Button
              sx={{
                visibility: 'none',
                height: scale(26),
                '&:hover': {
                  cursor: 'pointer',
                }
              }}
              onClick={() => setAddNotaDialogOpen(true)}
            >
              <Text
                text={'Aggiungi nota'}
                color={theme.palette.text.secondary}
                fontFamily={FONTS.MONTSERRAT}
                fontWeight={600}
                fontSize={scale(18)}
                sx={{textTransform: 'uppercase', textDecoration: 'underline'}}
              />
            </Button>)}
            <Button
              sx={{
                visibility: 'none',
                height: scale(26),
                '&:hover': {
                  cursor: 'pointer',
                }
              }}
              onClick={() => setDeleteRaccoltaDialogOpen(true)}
            >
              <Text
                text={'elimina'}
                color={theme.palette.text.secondary}
                fontFamily={FONTS.MONTSERRAT}
                fontWeight={600}
                fontSize={scale(18)}
                sx={{textTransform: 'uppercase', textDecoration: 'underline'}}
              />
            </Button>
            <Button
              sx={{
                visibility: 'none',
                height: scale(26),
                '&:hover': {
                  cursor: 'pointer',
                }
              }}
              onClick={handleDownload}
            >
              <Text
                text={'scarica dati'}
                color={theme.palette.text.secondary}
                fontFamily={FONTS.MONTSERRAT}
                fontWeight={600}
                fontSize={scale(18)}
                sx={{textTransform: 'uppercase', textDecoration: 'underline'}}
              />
            </Button>
          </BaseHeader>
          <BaseTitle title={LABEL.DETAIL_DONATION}/>
          <ContentWrapper>
            <Box style={{display: 'flex', flexDirection: 'column', gap: scale(20)}}>
              <DateDetailedCard
                dayName={dateInfo.weekday}
                dayNumber={dateInfo.day.toString()}
                month={dateInfo.month.concat('\n')}
                year={dateInfo.year.toString()}
                colorStyle={"secondary"}
                city={raccolta.name}
                firstHour={getFasciaRange(raccolta, 0)}
                secondHour={getFasciaRange(raccolta, 1)}
                address={raccolta.address}
                sx={{paddingBottom: raccolta.name.length > 14 ? scale(20) : 0}}
              />
              {(raccolta.type === 'sangue' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.blood.main}
                colorStyle={'primary'}
                firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Sangue'}
              />)}
              {(raccolta.type === 'plasma' || raccolta.type === 'plasma & sangue') && (<DateRowInfo
                IconComponent={
                  <CustomSvg
                    svg={GOCCIA_RIFLESSO}
                    sx={{
                      zIndex: 2,
                      fontSize: scale(36),
                    }}
                    color={'white'}
                  />
                }
                bgColor={theme.palette.plasma.main}
                colorStyle={'primary'}
                firstRowText={(raccolta.postiDisponibili ? raccolta.postiDisponibili.toString().concat(' ') : '') + 'Postazioni'}
                secondRowText={'Plasma'}
              />)}
            </Box>

            <DonorsWrapper>
              <Text
                text={'lista donatori'}
                color={theme.palette.text.secondary}
                fontFamily={FONTS.MONTSERRAT}
                fontWeight={'bold'}
                fontSize={scale(24)}
                sx={{textTransform: 'uppercase', alignSelf: 'center'}}
              />
              <Box
                sx={{
                  backgroundColor: theme.palette.text.secondary,
                  height: scale(2),
                  width: '100%',
                  marginTop: scale(10),
                }}
              />
              <DonorSearch
                textSearch={textSearch}
                setTextSearch={(value) => setTextSearch(value)}
                activeFilter={filter}
                setActiveFilter={setFilter}
              />
              <DonorList
                textSearch={textSearch}
                filter={filter}
                listaDonatori={listaDonatori}
                onExportCSV={setGetCSVBlob}
                onDelete={(prenotazioneId, raccoltaFasciaTurnoPrenotazioneId) => {
                  setDeleteDonorData({
                    prenotazioneId: prenotazioneId,
                    raccoltaFasciaTurnoPrenotazioneId: raccoltaFasciaTurnoPrenotazioneId,
                  })
                  setDeleteDonorDialogOpen(true);
                }}/>
            </DonorsWrapper>
          </ContentWrapper>
        </Base>
      </Wrapper>
    </>
  );
}

export default DettaglioRaccolta;
